import { Order } from 'components/pages/order-details';
import * as React from 'react';
import {
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { getActions, OrderDetailsActions } from './actions';
import { orderDetailReducer, OrderDetailsAction } from './reducer';
import { getOrder } from './service';
import { ActionType, LoadingType, OrderDetailsState } from './types';

export const defaultOrderDetailsState: OrderDetailsState = {
  loading: {
    [LoadingType.GetOverview]: false,
    [LoadingType.GetTransactions]: false,
    [LoadingType.GetPaymentSchedule]: false,
    [LoadingType.GetNotes]: false,
    [LoadingType.GetOrder]: false,
    [LoadingType.CancelFees]: false,
    [LoadingType.RefundTransaction]: false,
    [LoadingType.GetInstallments]: false,
    [LoadingType.UpdateInstallment]: false,
    [LoadingType.CreateOfflinePayment]: false,
    [LoadingType.GetPreviewLineItems]: false,
    [LoadingType.SaveAdjustment]: false,
  },
  order: null,
  orgId: null,
  orderLineItems: [],
  previewLineItems: null,
  transactions: [],
  installments: [],
  notes: [],
  error: '',
};

const OrderDetailsContext = createContext<OrderDetailsState>(
  defaultOrderDetailsState
);
const DispatchContext = createContext<Dispatch<OrderDetailsAction>>(undefined);

export const OrderDetailsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(
    orderDetailReducer,
    defaultOrderDetailsState
  );

  return (
    <OrderDetailsContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </OrderDetailsContext.Provider>
  );
};

export const useOrderDetails = (
  orgId?: number,
  orderNumber?: string
): OrderDetailsState => {
  const context = useContext(OrderDetailsContext);
  const dispatch = useContext(DispatchContext);

  useEffect(() => {
    if (!orgId || context.orgId === orgId) {
      return;
    }
    dispatch({ type: ActionType.SetOrgId, payload: { orgId } });
  }, [orgId]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderNumber || context.order?.orderNumber === orderNumber) {
        return;
      }
      try {
        // Fetch order on hook mount
        dispatch({
          type: ActionType.SetLoading,
          loading: { loadingType: LoadingType.GetOrder, isLoading: true },
        });
        const { data: order } = await getOrder<{ showOrder: Order }>(
          orderNumber
        );
        dispatch({
          type: ActionType.SetOrder,
          payload: { order: order.showOrder },
        });
      } catch (error) {
        dispatch({
          type: ActionType.SetError,
          payload: { error: error.message },
        });
      } finally {
        dispatch({
          type: ActionType.SetLoading,
          loading: {
            loadingType: LoadingType.GetOrder,
            isLoading: false,
          },
        });
      }
    };
    fetchOrder();
  }, [orderNumber]);

  if (context === undefined) {
    throw new Error(
      'useOrderDetails must be used within a OrderDetailsProvider'
    );
  }
  return context;
};

export const useOrderDetailsActions = (): OrderDetailsActions => {
  const dispatch = useContext(DispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useOrderDetailsActions must be used within a OrderDetailsProvider'
    );
  }
  return getActions(dispatch);
};
